// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing — the routing engine's console, for spotting trouble and helping a skipper
// fast: the engine's health first (containers, queue, failures, forecasts, cost), then every
// run of the last 48 hours, live. One run opens at a time to show its request, its container,
// its timing and its cost, with Stop, Restart, Delete and the queue moves; its route is drawn
// only when asked. New runs are made on Admin › Routing › New run (AdminRoutingNewRunPage).

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatLocal } from './weatherFormat';
import { ResultView } from './AdminRoutingNewRunPage';
import { Card, Pill } from './routingUi';
import type { Tone } from './routingFormat';
import { clock, healthView, isOpen, money, originView, progressView, runTimes, type HealthTile } from './routingConsoleFormat';
import { api, type RoutingConsoleJob, type RoutingCosts, type RoutingHealth, type RoutingJobState, type RoutingResult } from '../../services/api';

const HOURS = 48;
/** Poll fast while something runs or waits, slowly when the engine is idle. */
const BUSY_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;
const COST_POLL_MS = 60_000;

const STATE: Record<RoutingJobState, { tone: Tone; text: string }> = {
  running: { tone: 'info', text: 'Running' },
  queued: { tone: 'warn', text: 'Waiting' },
  failed: { tone: 'err', text: 'Failed' },
  done: { tone: 'ok', text: 'Done' },
};
const KIND: Record<string, string> = { route: 'One route', evaluate: 'Compare models', window: 'Departure window', replay: 'Skipper’s own line' };
const FILTERS: { id: 'all' | RoutingJobState; name: string }[] = [
  { id: 'all', name: 'All' },
  { id: 'running', name: 'Running' },
  { id: 'queued', name: 'Waiting' },
  { id: 'failed', name: 'Failed' },
  { id: 'done', name: 'Done' },
];
const TILE_EDGE: Record<Tone, string> = { ok: 'border-t-transparent', info: 'border-t-transparent', mute: 'border-t-transparent', warn: 'border-t-amber-400', err: 'border-t-red-400' };
const LEVEL: Record<Tone, { dot: string; text: string }> = {
  ok: { dot: 'bg-emerald-400', text: 'Routing engine healthy' },
  info: { dot: 'bg-emerald-400', text: 'Routing engine healthy' },
  mute: { dot: 'bg-emerald-400', text: 'Routing engine healthy' },
  warn: { dot: 'bg-amber-400', text: 'Routing engine needs a look' },
  err: { dot: 'bg-red-400', text: 'Routing engine has a problem' },
};

const nameOf = (j: RoutingConsoleJob) => j.label ?? KIND[j.kind] ?? 'Unnamed run';
const btn = 'px-3 py-1.5 rounded-lg border text-[14px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed';
const plain = `${btn} border-slate-600 text-slate-200 hover:bg-slate-700`;
const danger = `${btn} border-red-500/50 text-red-300 hover:bg-red-500/10`;

type Action = 'stop' | 'restart' | 'delete' | 'force';

export function AdminRoutingPage() {
  const [data, setData] = useState<{ health: RoutingHealth; jobs: RoutingConsoleJob[] } | null>(null);
  const [readAt, setReadAt] = useState<number | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [costs, setCosts] = useState<RoutingCosts | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | RoutingJobState>('all');
  const [query, setQuery] = useState('');
  const [routeOf, setRouteOf] = useState<RoutingConsoleJob | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      setData(await api.adminRoutingOverview(HOURS));
      setReadAt(Date.now());
      setReadError(null);
    } catch {
      setReadError('The routing engine could not be read just now. The page tries again shortly.');
    }
  }, []);

  const loadCosts = useCallback(async () => {
    try {
      setCosts(await api.adminRoutingCosts(HOURS));
    } catch {
      setCosts({ available: false, reason: 'The costs could not be read just now.' });
    }
  }, []);

  const busy = !!data?.jobs.some(isOpen);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const t = setTimeout(load, busy ? BUSY_POLL_MS : IDLE_POLL_MS);
    return () => clearTimeout(t);
  }, [load, busy, readAt]);
  useEffect(() => {
    void loadCosts();
    const t = setInterval(loadCosts, COST_POLL_MS);
    return () => clearInterval(t);
  }, [loadCosts]);
  // Running times and "updated … ago" move every second.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  const jobs = useMemo(() => data?.jobs ?? [], [data]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => (filter === 'all' || j.state === filter) && (!q || [nameOf(j), j.id, j.origin?.email, j.origin?.who, j.error].some((s) => s?.toLowerCase().includes(q))));
  }, [jobs, filter, query]);

  const toggle = (id: string) => setOpenId((o) => (o === id ? null : id));
  const done = (text: string) => {
    setNotice(text);
    void load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Routing</h1>
          <p className="text-[14px] text-slate-400 mt-1">The engine’s health and every run of the last {HOURS} hours. Open a run to see its detail and act on it.</p>
        </div>
        <Link to="/admin/routing/new" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[15px] font-semibold">
          <PlusIcon className="w-5 h-5" /> New run
        </Link>
      </div>

      {readError && <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-[14px] text-red-200">{readError}</p>}

      {data && <HealthPanel health={data.health} jobs={jobs} costs={costs} now={now} readAt={readAt} />}

      <Card
        title={`Runs · last ${HOURS} hours`}
        caption="Newest first. Click a run to open it; one is open at a time."
        action={
          <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filter runs">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-full border text-[13px] font-semibold tabular-nums ${filter === f.id ? 'border-cyan-500/60 bg-cyan-500/10 text-cyan-300' : 'border-slate-600 text-slate-400 hover:bg-slate-700'}`}
              >
                {f.name} {f.id === 'all' ? jobs.length : jobs.filter((j) => j.state === f.id).length}
              </button>
            ))}
            <input
              type="search"
              aria-label="Find a run by skipper, boat or name"
              placeholder="Find a skipper, boat or run"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-56 max-w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-1.5 text-[14px] text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>
        }
      >
        {notice && (
          <p role="status" className="px-5 py-2 text-[14px] text-cyan-200 bg-cyan-500/10 border-b border-slate-700 flex justify-between gap-4">
            {notice}
            <button type="button" className="text-slate-400 hover:text-white" aria-label="Dismiss" onClick={() => setNotice(null)}><XMarkIcon className="w-4 h-4" /></button>
          </p>
        )}
        {!data && !readError ? (
          <p className="px-5 py-4 text-[14px] text-slate-400">Reading the engine…</p>
        ) : shown.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-[14px]">
              <thead>
                <tr className="text-left text-slate-400 text-[13px]">
                  {['Status', 'Run', 'For', 'Started', 'Time', 'Container', 'Tries', 'Cost'].map((h) => <th key={h} className="px-4 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {shown.map((j) => (
                  <RunRows
                    key={j.id}
                    job={j}
                    open={openId === j.id}
                    onToggle={() => toggle(j.id)}
                    cost={costs?.available ? costs.jobs[j.id] : undefined}
                    now={now}
                    onShowRoute={() => setRouteOf(j)}
                    onDone={done}
                    onRestarted={(next) => {
                      setOpenId(next);
                      done('Restarted · waiting to run');
                    }}
                    onDeleted={() => {
                      setOpenId(null);
                      done('Deleted');
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-5 py-4 text-[14px] text-slate-400">{jobs.length ? 'No runs match.' : `No runs in the last ${HOURS} hours.`}</p>
        )}
      </Card>

      <RouteDialog job={routeOf} onClose={() => setRouteOf(null)} />
    </div>
  );
}

function HealthPanel({ health, jobs, costs, now, readAt }: { health: RoutingHealth; jobs: RoutingConsoleJob[]; costs: RoutingCosts | null; now: number; readAt: number | null }) {
  const view = healthView(health, jobs, now);
  const cost: HealthTile = costs?.available
    ? { key: 'cost', label: `Cost · ${HOURS} h`, value: money(costs.totalUsd), detail: `Runs ${money(costs.totalUsd - costs.idleUsd)} · idle engine ${money(costs.idleUsd)}`, tone: 'ok' }
    : { key: 'cost', label: `Cost · ${HOURS} h`, value: '—', detail: costs ? 'Not available' : 'Reading…', tone: 'ok' };
  const level = LEVEL[view.level];
  return (
    <section aria-label="Routing engine health" className="rounded-xl border border-slate-700 bg-slate-800/50">
      <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-[17px] font-bold text-white flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${level.dot}`} aria-hidden="true" />
          {level.text}
        </h2>
        <span className="text-[13px] text-slate-400 tabular-nums">{readAt ? `Live · updated ${clock(now - readAt)} ago` : 'Live'}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8">
        {[...view.tiles, cost].map((t) => (
          <div key={t.key} className={`px-4 py-3 border-t-[3px] ${TILE_EDGE[t.tone]} border-r border-b border-r-slate-700 border-b-slate-700 min-w-0`}>
            <p className="text-[13px] uppercase tracking-wide text-slate-400">{t.label}</p>
            <p className="text-[20px] font-bold text-white tabular-nums">{t.value}</p>
            <p className="text-[13px] text-slate-400 break-words">{t.detail}</p>
          </div>
        ))}
      </div>
      {(view.alerts.length > 0 || (costs && !costs.available)) && (
        <ul className="px-5 py-3 space-y-1.5">
          {view.alerts.map((a) => (
            <li key={a.text} className="text-[14px] text-slate-200 flex gap-3 items-baseline">
              <span className={`text-[13px] font-bold uppercase tracking-wide ${a.tone === 'err' ? 'text-red-300' : 'text-amber-300'}`}>{a.tone === 'err' ? 'Problem' : 'Check'}</span>
              {a.text}
            </li>
          ))}
          {costs && !costs.available && (
            <li className="text-[14px] text-slate-400 flex gap-3 items-baseline">
              <span className="text-[13px] font-bold uppercase tracking-wide text-slate-400">Cost</span>
              {costs.reason}
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

interface RunProps {
  job: RoutingConsoleJob;
  open: boolean;
  onToggle(): void;
  cost: { usd: number; cpuSec: number; memGiBSec: number; diskGBSec: number; basis: 'measured' | 'estimate' } | undefined;
  now: number;
  onShowRoute(): void;
  onDone(text: string): void;
  onRestarted(nextId: string): void;
  onDeleted(): void;
}

function RunRows({ job: j, open, onToggle, cost, now, onShowRoute, onDone, onRestarted, onDeleted }: RunProps) {
  const s = STATE[j.state];
  const o = originView(j.origin);
  const t = runTimes(j, now);
  const prog = progressView(j.live);
  return (
    <>
      <tr
        aria-expanded={open}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`border-t border-slate-700 cursor-pointer align-top focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${open ? 'bg-slate-700/40' : 'hover:bg-slate-700/20'} ${j.state === 'failed' ? 'shadow-[inset_3px_0_0_#f87171]' : ''}`}
      >
        <td className="px-4 py-2.5 whitespace-nowrap">
          <span className={`inline-block w-3 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true">›</span>{' '}
          <Pill tone={s.tone}>{j.state === 'running' && <span className="w-2 h-2 rounded-full bg-current animate-pulse motion-reduce:animate-none" />}{s.text}</Pill>
        </td>
        <td className="px-4 py-2.5 min-w-0">
          <p className="font-semibold text-white">{nameOf(j)}</p>
          <p className="text-[13px] text-slate-400">{KIND[j.kind] ?? j.kind}{j.error && <> · <span className="text-red-300">{j.error}</span></>}</p>
        </td>
        <td className="px-4 py-2.5 max-w-[15rem]">
          <p className="text-slate-200">{o.from}</p>
          <p className="text-[13px] text-slate-400 truncate" title={o.who}>{o.who}</p>
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap text-slate-200">{formatLocal(j.submittedMs)}</td>
        <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">
          {j.state === 'running' ? (
            <>
              <p className="text-slate-200">{clock(t.runMs)}</p>
              {prog && (
                <>
                  <div className="w-32 h-1.5 rounded bg-slate-700 overflow-hidden mt-1" aria-hidden="true"><div className="h-full bg-cyan-400" style={{ width: `${Math.min(100, Math.round(prog.fraction * 100))}%` }} /></div>
                  <p className="text-[13px] text-slate-400">{prog.text}</p>
                </>
              )}
            </>
          ) : j.state === 'queued' ? (
            <>
              <p className="text-slate-200">waiting {clock(t.waitMs)}</p>
              {typeof j.live?.ahead === 'number' && <p className="text-[13px] text-slate-400">{j.live.ahead ? `${j.live.ahead} ahead` : 'next to run'}</p>}
            </>
          ) : (
            <p className="text-slate-200">{clock(t.totalMs)}</p>
          )}
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          <p className={j.own ? 'text-violet-300' : 'text-cyan-300'}>{j.own ? 'Own' : 'Shared'}</p>
          <p className="text-[13px] text-slate-400 font-mono">{j.own ? `job-${j.id.slice(0, 8)}` : j.container}</p>
        </td>
        <td className={`px-4 py-2.5 tabular-nums ${j.attempts > 1 ? 'text-amber-300 font-semibold' : 'text-slate-200'}`}>{j.attempts}</td>
        <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">
          <p className="text-slate-200">{money(cost?.usd)}</p>
          {cost?.basis === 'estimate' && <p className="text-[13px] text-slate-400">est.</p>}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-900/70">
          <td colSpan={8} className="px-4 py-0">
            <RunDetail job={j} cost={cost} now={now} onShowRoute={onShowRoute} onDone={onDone} onRestarted={onRestarted} onDeleted={onDeleted} />
          </td>
        </tr>
      )}
    </>
  );
}

function Facts({ title, rows, note }: { title: string; rows: [string, React.ReactNode][]; note?: string }) {
  return (
    <div className="min-w-0">
      <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-1">{title}</h3>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-[14px]">
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <dt className="text-slate-400">{k}</dt>
            <dd className="text-slate-200 min-w-0 break-words">{v}</dd>
          </Fragment>
        ))}
      </dl>
      {note && <p className="text-[13px] text-slate-400 mt-1">{note}</p>}
    </div>
  );
}

function RunDetail({ job: j, cost, now, onShowRoute, onDone, onRestarted, onDeleted }: Omit<RunProps, 'open' | 'onToggle'>) {
  const [confirm, setConfirm] = useState<{ action: Action; text: string; verb: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const o = originView(j.origin);
  const t = runTimes(j, now);
  const forSkipper = j.origin?.source === 'skipper' || j.origin?.source === 'check';

  const ask = (action: Action) => {
    setProblem(null);
    if (action === 'stop') setConfirm({ action, verb: 'Stop run', text: `Stop “${nameOf(j)}”? The engine drops it now.${forSkipper ? ' The skipper sees that an admin stopped it.' : ''}` });
    if (action === 'restart') setConfirm({ action, verb: 'Restart run', text: `Run “${nameOf(j)}” again from the start on the latest forecasts?${isOpen(j) ? ' It is stopped first.' : ''}` });
    if (action === 'delete') setConfirm({ action, verb: 'Delete run', text: 'Delete this run’s record and its stored result? This cannot be undone.' });
  };

  async function act(action: Action) {
    setBusy(true);
    setProblem(null);
    try {
      if (action === 'stop') {
        await api.adminRoutingCancel(j.id);
        setConfirm(null);
        onDone(`Stopped “${nameOf(j)}”`);
      } else if (action === 'restart') {
        const next = await api.adminRoutingRestart(j.id);
        setConfirm(null);
        onRestarted(next.id);
      } else {
        const r = await api.adminRoutingDelete(j.id, action === 'force');
        if (r.deleted) {
          setConfirm(null);
          onDeleted();
        } else {
          const parts = [
            r.uses.departureChecks ? `${r.uses.departureChecks} departure check${r.uses.departureChecks > 1 ? 's' : ''}` : '',
            r.uses.commentaries ? `${r.uses.commentaries} passage commentar${r.uses.commentaries > 1 ? 'ies' : 'y'}` : '',
          ].filter(Boolean);
          setConfirm({ action: 'force', verb: 'Delete anyway', text: `${parts.join(' and ')} still show this run. Deleting it removes the route they show.` });
        }
      }
    } catch (e) {
      const data = (e as { response?: { data?: { message?: string; error?: string } } }).response?.data;
      setProblem(`That did not work: ${data?.message ?? data?.error ?? (e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function move(to: 'front' | 'back') {
    try {
      await api.adminRoutingMove(j.id, to);
      onDone(to === 'front' ? 'Moved to the front: it runs next' : 'Moved to the back of the queue');
    } catch (e) {
      setProblem(`That did not work: ${(e as Error).message}`);
    }
  }

  async function copyId() {
    try {
      await navigator.clipboard.writeText(j.id);
      onDone('Job ID copied');
    } catch {
      setProblem(`Job ID: ${j.id}`);
    }
  }

  return (
    <div className="py-3 pl-6 space-y-3" onClick={(e) => e.stopPropagation()}>
      {j.error && <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[14px] text-red-200 font-mono break-words">{j.error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
        <Facts
          title="Request"
          rows={[
            ['Asked by', o.who],
            ['From', o.from],
            ...(o.boat ? ([['Boat', o.boat]] as [string, React.ReactNode][]) : []),
            ['Run', KIND[j.kind] ?? j.kind],
            ['Asked', formatLocal(j.submittedMs)],
          ]}
        />
        <Facts
          title="Engine"
          rows={[
            ['Container', <span key="c" className="font-mono text-[13px] break-all">{j.own ? `Its own · ${j.container}` : 'Shared engine'}</span>],
            ['Time', j.state === 'queued' ? `waiting ${clock(t.waitMs)}` : `waited ${clock(t.waitMs)} · worked ${clock(t.runMs)}`],
            ...(j.live?.stage ? ([['Stage', j.live.stage]] as [string, React.ReactNode][]) : []),
            ['Tries', String(j.attempts)],
            ['Job ID', <span key="i" className="font-mono text-[13px] break-all">{j.id}</span>],
          ]}
        />
        <Facts
          title="Usage and cost"
          rows={cost ? [
            ['CPU', `${Math.round(cost.cpuSec)} vCPU-s`],
            ['Memory', `${Math.round(cost.memGiBSec)} GiB-s`],
            ['Disk', `${Math.round(cost.diskGBSec)} GB-s`],
            ['Total', <span key="t" className="inline-flex items-center gap-2"><b>{money(cost.usd)}</b><Pill tone={cost.basis === 'measured' ? 'ok' : 'mute'}>{cost.basis === 'measured' ? 'measured' : 'estimate'}</Pill></span>],
          ] : [['Cost', j.state === 'queued' ? 'None yet' : 'Not known yet']]}
          note={cost ? (cost.basis === 'measured' ? 'Cloudflare’s figures for its own container.' : 'Its share of the shared engine’s minutes while it ran.') : 'Cloudflare reports usage a few minutes after it happens.'}
        />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" className={plain} disabled={j.state !== 'done'} onClick={onShowRoute}>Show route</button>
        {isOpen(j) && <button type="button" className={danger} disabled={busy} onClick={() => ask('stop')}>Stop</button>}
        {j.state === 'queued' && !j.own && (
          <>
            <button type="button" className={plain} onClick={() => void move('front')}>Run next</button>
            <button type="button" className={plain} onClick={() => void move('back')}>Move to back</button>
          </>
        )}
        {j.state !== 'queued' && <button type="button" className={plain} disabled={busy} onClick={() => ask('restart')}>Restart</button>}
        {!isOpen(j) && <button type="button" className={danger} disabled={busy} onClick={() => ask('delete')}>Delete</button>}
        <button type="button" className={plain} onClick={() => void copyId()}>Copy job ID</button>
        {j.origin?.email && <Link to={`/admin/users?search=${encodeURIComponent(j.origin.email)}`} className={plain}>Open customer</Link>}
      </div>
      {confirm && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[14px] text-amber-100">
          <span className="flex-1 min-w-[240px]">{confirm.text}</span>
          <button type="button" className={danger} disabled={busy} onClick={() => void act(confirm.action)}>{busy ? 'Working…' : confirm.verb}</button>
          <button type="button" className={plain} disabled={busy} onClick={() => setConfirm(null)}>Keep it</button>
        </div>
      )}
      {problem && <p role="alert" className="text-[14px] text-red-300 break-all">{problem}</p>}
    </div>
  );
}

function RouteDialog({ job, onClose }: { job: RoutingConsoleJob | null; onClose(): void }) {
  const [result, setResult] = useState<RoutingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!job) return;
    let gone = false;
    setResult(null);
    setError(null);
    api.adminRoutingResult(job.id).then(
      (r) => !gone && setResult(r),
      (e: Error) => !gone && setError(`The route could not be read: ${e.message}`),
    );
    return () => {
      gone = true;
    };
  }, [job]);
  return (
    <Transition show={!!job} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <div className="fixed inset-0 bg-slate-950/75" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4 flex items-start justify-center">
          <Dialog.Panel className="w-full max-w-5xl rounded-xl border border-slate-700 bg-slate-800 shadow-xl">
            <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between gap-4">
              <Dialog.Title className="text-[17px] font-bold text-white">{job ? nameOf(job) : ''}</Dialog.Title>
              <button type="button" className={plain} onClick={onClose}>Close</button>
            </div>
            <div className="p-5">
              {error ? <p className="text-[14px] text-red-300">{error}</p> : result ? <ResultView r={result} /> : <p className="text-[14px] text-slate-400">Reading the route…</p>}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
}
